import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { toast } from 'react-toastify';

const AuditLogsTable = () => {
    const { auth } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Pagination
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Filters
    const [action, setAction] = useState('');
    const [targetType, setTargetType] = useState('');
    const [userId, setUserId] = useState('');
    const [adminId, setAdminId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Dropdown options
    const [availableActions, setAvailableActions] = useState([]);
    const [availableTargetTypes, setAvailableTargetTypes] = useState([]);

    // Fetch Filters (Actions & Target Types)
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/audit-logs/filters`);
                if (response.ok) {
                    const data = await response.json();
                    setAvailableActions(data.actions || []);
                    setAvailableTargetTypes(data.target_types || []);
                }
            } catch (error) {
                console.error('Error fetching filters:', error);
            }
        };
        fetchFilters();
    }, [auth]);

    // Fetch Logs
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit,
                ...(action && { action }),
                ...(targetType && { target_type: targetType }),
                ...(userId && { user_id: userId }),
                ...(adminId && { admin_id: adminId }),
                ...(startDate && { start_date: startDate }),
                ...(endDate && { end_date: endDate })
            });

            const response = await auth.fetch(`${config.API_BASE_URL}/api/audit-logs?${queryParams}`);
            if (response.ok) {
                const data = await response.json();
                setLogs(data.data || []);
                setTotalPages(data.pagination.totalPages);
                setTotal(data.pagination.total);
            } else {
                toast.error('Failed to fetch audit logs');
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            toast.error('Error loading logs');
        } finally {
            setLoading(false);
        }
    }, [auth, page, limit, action, targetType, userId, adminId, startDate, endDate]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const clearFilters = () => {
        setAction('');
        setTargetType('');
        setUserId('');
        setAdminId('');
        setStartDate('');
        setEndDate('');
        setPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const formatDetails = (oldVal, newVal) => {
        if (!oldVal && !newVal) return '-';
        try {
            const oldObj = typeof oldVal === 'string' ? JSON.parse(oldVal) : oldVal;
            const newObj = typeof newVal === 'string' ? JSON.parse(newVal) : newVal;
            
            return (
                <div className="text-xs">
                    {oldObj && (
                        <div className="mb-1">
                            <span className="fw-bold text-danger">Old:</span> 
                            <span className="text-truncate d-inline-block ms-1" style={{maxWidth: '200px'}} title={JSON.stringify(oldObj)}>
                                {JSON.stringify(oldObj)}
                            </span>
                        </div>
                    )}
                    {newObj && (
                        <div>
                            <span className="fw-bold text-success">New:</span>
                            <span className="text-truncate d-inline-block ms-1" style={{maxWidth: '200px'}} title={JSON.stringify(newObj)}>
                                {JSON.stringify(newObj)}
                            </span>
                        </div>
                    )}
                </div>
            );
        } catch (e) {
            return <span className="text-xs text-muted">Raw data view unavailable</span>;
        }
    };

    return (
        <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
                <h5 className="text-lg fw-semibold mb-0">System Audit Logs</h5>
                <button 
                    className="btn btn-outline-primary-600 text-sm btn-sm px-12 py-6 d-flex align-items-center gap-2"
                    onClick={fetchLogs}
                >
                    <Icon icon="mdi:refresh" className="text-xl" />
                    Refresh
                </button>
            </div>
            
            {/* Filters Section */}
            <div className="card-body p-24 pb-0">
                <div className="row gy-3">
                    <div className="col-md-2">
                        <label className="form-label text-sm fw-semibold text-primary-light">Action</label>
                        <select 
                            className="form-control form-select text-sm"
                            value={action}
                            onChange={(e) => setAction(e.target.value)}
                        >
                            <option value="">All Actions</option>
                            {availableActions.map(a => (
                                <option key={a} value={a}>{a}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label text-sm fw-semibold text-primary-light">Target Type</label>
                        <select 
                            className="form-control form-select text-sm"
                            value={targetType}
                            onChange={(e) => setTargetType(e.target.value)}
                        >
                            <option value="">All Types</option>
                            {availableTargetTypes.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label text-sm fw-semibold text-primary-light">Start Date</label>
                        <input 
                            type="date" 
                            className="form-control text-sm"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label text-sm fw-semibold text-primary-light">End Date</label>
                        <input 
                            type="date" 
                            className="form-control text-sm"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label text-sm fw-semibold text-primary-light">User ID</label>
                        <input 
                            type="number" 
                            className="form-control text-sm"
                            placeholder="User ID"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button 
                            className="btn btn-danger-600 text-sm w-100"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table mb-0 text-sm">
                        <thead>
                            <tr>
                                <th scope="col">Date/Time</th>
                                <th scope="col">Admin/Actor</th>
                                <th scope="col">Action</th>
                                <th scope="col">Target</th>
                                <th scope="col">Affected User</th>
                                <th scope="col">Details (Old/New)</th>
                                <th scope="col">IP Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-24">
                                        <div className="spinner-border text-primary-600" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-24 text-secondary-light">
                                        No logs found matching criteria.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id}>
                                        <td className="text-nowrap">{formatDate(log.created_at)}</td>
                                        <td>
                                            {log.admin_name ? (
                                                <div className="d-flex flex-column">
                                                    <span className="fw-semibold text-primary-600">{log.admin_name}</span>
                                                    <span className="text-xs text-secondary-light">{log.admin_email}</span>
                                                </div>
                                            ) : (
                                                <span className="text-secondary-light">System/User</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge bg-primary-50 text-primary-600 px-8 py-4 rounded-pill">
                                                {log.action}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="fw-semibold">{log.target_type}</span>
                                                <span className="text-xs">ID: {log.target_id}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {log.user_name ? (
                                                <div className="d-flex flex-column">
                                                    <span className="fw-semibold">{log.user_name}</span>
                                                    <span className="text-xs text-secondary-light">{log.user_email}</span>
                                                </div>
                                            ) : (
                                                <span className="text-secondary-light">-</span>
                                            )}
                                        </td>
                                        <td style={{minWidth: '250px'}}>
                                            {formatDetails(log.old_value, log.new_value)}
                                        </td>
                                        <td>{log.ip_address || '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && logs.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between mt-24">
                        <span className="text-sm text-secondary-light">
                            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
                        </span>
                        <ul className="pagination mb-0">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(page - 1)}>
                                    <Icon icon="heroicons:chevron-left-20-solid" />
                                </button>
                            </li>
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                // Logic to show sliding window of pages could go here, simplified for now
                                let p = i + 1;
                                if (totalPages > 5 && page > 3) {
                                    p = page - 2 + i;
                                }
                                if (p > totalPages) return null;
                                
                                return (
                                    <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(p)}>
                                            {p}
                                        </button>
                                    </li>
                                );
                            })}
                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(page + 1)}>
                                    <Icon icon="heroicons:chevron-right-20-solid" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuditLogsTable;
